import React, { useCallback, useState } from 'react';
import axios from 'axios';

import { API_BASE_URL } from '@techchange/components';

const axiosClient = axios.create({
	baseURL: API_BASE_URL.INTEGRATIONS,
	withCredentials: true,
});

/**
 * Fetches and manages state related to a Lokalise translation project.
 *
 * @returns {Object} -
 *
 * @returns {Object}  -.apiStatusProject          State related to project API calls.
 * @returns {Object}  -.apiStatusProject.project  The API response data.
 * @returns {Boolean} -.apiStatusProject.saving   True, if API calls are in progress.
 * @returns {Boolean} -.apiStatusProject.success  True, if API calls have succeeded.
 * @returns {Boolean} -.apiStatusProject.error    True, if API calls have failed.
 *
 * @returns {Object}  -.apiStatusUploadFiles               State related to file upload API calls.
 * @returns {Object}  -.apiStatusUploadFiles.savingStatus  The API response data.
 * @returns {Boolean} -.apiStatusUploadFiles.saving        True, if API calls are in progress.
 * @returns {Boolean} -.apiStatusUploadFiles.success       True, if API calls have succeeded.
 * @returns {Boolean} -.apiStatusUploadFiles.error         True, if API calls have failed.
 *
 * @returns {Object}   -.projectActions              The project actions.
 * @returns {Function} -.projectActions.syncProject  Callback function to create/update a project.
 * @returns {Function} -.projectActions.uploadFiles  Callback function to upload files to a project.
 */
const useLokaliseTranslationProject = () => {

	const [apiStatusProject, setApiStatusProject] = useState({
		project: null,
		saving: false,
		success: false,
		error: false,
	});

	const [apiStatusUploadFiles, setApiStatusUploadFiles] = useState({
		saving: false,
		savingStatus: {},
		success: false,
		error: false,
	});

	/** API requests for managing the settings for a Lokalise translation project.
	 *
	 * @param {Object} data                   The request data.
	 * @param {String} data.base_language     The base language.
	 * @param {Array}  data.target_languages  The target languages.
	 * @param {String} data.scope             The scope.
	 * @param {String} data.scope_id          The scope ID.
	 *
	 * @returns {Promise<AxiosResponse>} Promise containing response from the API requests
	 */
	const syncProject = useCallback(async (data) => {

		// Reset the success and error state
		setApiStatusProject({
			project: null,
			saving: true,
			success: false,
			error: false,
		});

		// Reset the success and error state
		setApiStatusUploadFiles({
			saving: false,
			savingStatus: {},
			success: false,
			error: false,
		});

		let updatedProject = null;

		try {

			const { data: fetchedProject } = await axiosClient.get('/lokalise/project', {
				params: {
					scope: data.scope,
					scope_id: data.scope_id,
				}
			});

			updatedProject = fetchedProject;

		// Handle and ignore 404 error
		} catch (error) {
			console.error(error);
		}

		try {

			// Check if the Lokalise project already exists for this scope
			if (updatedProject) {

				// If so, update the Lokalise project settings

				const patchResponse = await axiosClient.patch('/lokalise/project', data);

				updatedProject = patchResponse.data;

			// Otherwise, create a new Lokalise project
			} else {

				const postResponse = await axiosClient.post('/lokalise/project', data);

				updatedProject = postResponse.data;

			}

			setApiStatusProject((prev) => ({
				...prev,
				project: updatedProject,
				saving: false,
				success: true,
			}));

			return updatedProject;

		} catch (error) {

			console.error(error);

			setApiStatusProject((prev) => ({ ...prev, saving: false, error: true }));

			return null;

		}

	}, []);

	/**
	 * POST API request for fetching status of uploaded files to a Lokalise translation project.
	 *
	 * @param {Array} processIds  The list of process IDs for the file upload(s).
	 * @param {String} scope      The scope.
	 * @param {String} scopeId    The scope ID.
	 *
	 * @returns {Promise<AxiosResponse>} Promise containing file upload statuses.
	 */
	const checkFilesUploadedToProject = useCallback(async (uploadResults, scope, scopeId) => {

		try {

			const processIds = uploadResults.map((fileStatus) => fileStatus.process.process_id);

			const { data: result } = await axiosClient.get('/lokalise/project/files', {
				params: {
					process_ids: processIds,
					scope,
					scope_id: scopeId,
				},
			});

			const statusMap = {};

			let promisesResolved = true;

			result.forEach((lokaliseProcess) => {

				const language = uploadResults.find(
					(fileStatus) => fileStatus.process.process_id === lokaliseProcess.process_id
				)?.language;

				statusMap[language] = {
					process_id: lokaliseProcess.process_id,
					status: lokaliseProcess.status,
					message: lokaliseProcess.message,
				};

				// TODO: What error state should we show for cancelled or failed???

				promisesResolved = promisesResolved && (
					lokaliseProcess.status === 'finished' ||
					lokaliseProcess.status === 'cancelled' ||
					lokaliseProcess.status === 'failed'
				);

			});

			return [promisesResolved, statusMap];

		} catch (error) {

			console.error(error);

			return [true, {}];

		}

	}, []);

	/**
	 * POST API request for uploading files to a Lokalise translation project.
	 *
	 * @param {Object} data  The project data.
	 *
	 * @param {String} data.base_language     The base language.
	 * @param {Array}  data.target_languages  The target languages.
	 * @param {String} data.scope             The scope.
	 * @param {String} data.scope_id          The scope ID.
	 *
	 * @param {String} [data.base_source_type]  The type of the content to send.
	 * @param {String} [data.base_source_id]    The ID of the content to send.
	 *
	 * @param {Boolean} [data.include_existing_translations]
	 * True, if including existing translations.
	 *
	 * @param {Boolean} [data.replace_modified]
	 * True, if replacing modified content.
	 *
	 * @returns {Promise<AxiosResponse>} Promise containing response from POST request
	 */
	const uploadFiles = useCallback(async (data) => {

		// Reset the success and error state
		setApiStatusUploadFiles({
			saving: true,
			savingStatus: {},
			success: false,
			error: false,
		});

		try {

			const { data: uploadResults } = await axiosClient.post('/lokalise/project/files', data);

			// Check on the status of the uploaded files asynchronously in the background...

			for (let i = 0; i < 8; i++) {

				const [uploadResolved, uploadStatusMap] =
					await checkFilesUploadedToProject(uploadResults, data.scope, data.scope_id);

				console.debug(`------- recheck status i=${i} --------`);
				console.debug(`promisesResolved=${uploadResolved}`);

				setApiStatusUploadFiles((prev) => ({ ...prev, savingStatus: uploadStatusMap }));

				// If all the file uploads are done, then we're ready to return the results
				if (uploadResolved) { break; }

				console.debug(`waiting for ${1000 * Math.pow(2, i)} ms...`);

				// Otherwise, wait for some time and then recheck the status (max of 2 minutes)
				await new Promise(
					(resolve) => setTimeout(() => resolve(), 1000 * Math.pow(2, i))
				);

			}

			setApiStatusUploadFiles((prev) => ({ ...prev, saving: false, success: true }));

		} catch (error) {

			console.error(error);

			setApiStatusUploadFiles((prev) => ({ ...prev, saving: false, error: true }));

		}

	}, [checkFilesUploadedToProject]);

	return {
		apiStatusProject,
		apiStatusUploadFiles,
		projectActions: {
			syncProject,
			uploadFiles,
		},
	};

};

export default useLokaliseTranslationProject;
